import { ApiService, UserType } from '@shared/services/api.service';
import {
  EventTypes,
  FloorPlaceDto,
  FloorPresenceDto,
  FloorPresenceUserDto,
  UserRoomChangedEvent,
  FloorArriveEvent,
  FloorLeaveEvent,
  UserRoomAccessRequestEvent,
} from 'nexgen-types';
import { CxTickerMessageActionEvent } from 'nexgen-types';

import { useMultiFloorStateStore } from '../stores/MultiFloorStore';
import { useFloorManagerStore } from '../stores/FloorManagerStore';
import {
  useGuestStore,
  useAuthStore,
  useNotificationStore,
  useWorkplaceStore,
} from '@/host/stores';
import { useMeetingStore } from '@/host/stores';
import { useMyFloorStore } from '@/apps/office/stores/myFloorStore';
import { GlobalAudioNotificationService } from '@host/services/audio-notification.service';
import { voLogger } from '@shared/utils/logger';
import { GlobalEventService } from '@/host/services/event.service';
import { OfficeEventRouter } from '../events/OfficeEventRouter';
import { WorkplaceEventRouter } from '@/host/workplace/events/WorkplaceEventRouter';
import { forceGuestLogoutRedirect } from '@/utils/guestLogoutUtils';
import { GuestWorkplaceService } from '@/host/guest/services/GuestWorkplaceService';
import { updateSelfUserPresence } from './floorService';
import { useCxFloorStore } from '../views/nexgenCX/stores/CxFloorStore';
import { findRoomLockOwner, syncOwnCallLock } from '../views/nexgenCX/utils/roomLock';
import {
  fetchAgentStatuses,
  fetchLatestKpisForAllAgents,
} from '../views/nexgenCX/services/cxService';
import { isCurrentUserGuest } from '@/shared/utils/guest-detection.util';
import {
  isFloorPlaceTypeCcaas,
  isCurrentUserPlatformAdminOrOwner,
  isCurrentUserCcaasSupervisorOrAdminStatic,
} from '@/host/hooks/useLicenseFloorUtils';
import { useCxTickerMessageStore } from '../views/nexgenCX/stores/CxTickerMessageStore';
import { Logger } from '../../../shared/services/logger.service';
import {
  trackRoomAccessRequested,
  trackRoomNavigated,
  type RoomNavigationSource,
} from '@host/utils/office-tracking';

const _log = voLogger('office:service');

export class OfficeService {
  private static instance: OfficeService;
  officeEventRouter?: OfficeEventRouter; // Fixed property name and type

  // EventService.routeEvent fires handlers without awaiting them, so two room-change
  // events for the same floor arriving close together can each kick off a presence GET
  // and those GETs can resolve out of order. Tracking a per-floor sequence number lets
  // handlePresenceUpdate drop a result if a later-issued request already applied its own.
  private presenceRequestSeq = new Map<string, number>();
  private presenceAppliedSeq = new Map<string, number>();

  static getInstance(): OfficeService {
    if (!OfficeService.instance) {
      OfficeService.instance = new OfficeService();
    }
    return OfficeService.instance;
  }

  // Fixed parameter type
  setEventRouterInstance(eventRouterInstance: OfficeEventRouter) {
    this.officeEventRouter = eventRouterInstance;
  }

  /**
   * Check if a room is locked by its owner
   */
  private isRoomLocked(
    floorPid: string,
    roomPid: string,
    currentUserPid: string
  ): { isLocked: boolean; ownerName?: string; ownerPid?: string } {
    const floor = this.getStores().multiFloor.getFloorState(floorPid);
    const presence = floor?.presence;

    if (!presence) {
      return { isLocked: false };
    }

    // Find users in the target room
    const usersInRoom = presence.users.filter((user: any) => user.room === roomPid);

    // Shared with the room tint and the occupant rings so all three agree on what "locked"
    // means. getState() rather than a subscription: this runs on a click, not in a render.
    const lockedUser = findRoomLockOwner(usersInRoom, useCxFloorStore.getState().agentStatuses);

    if (lockedUser) {
      // If the locked user is the current user, they can still navigate (they're unlocking)
      if (lockedUser.pid === currentUserPid) {
        return { isLocked: false };
      }

      // Room is locked by someone else
      return {
        isLocked: true,
        ownerName: lockedUser.name || 'Another user',
        ownerPid: lockedUser.pid,
      };
    }

    return { isLocked: false };
  }

  /**
   * Send access request to locked room owner
   */
  private async sendRoomAccessRequest(payload: {
    roomPid: string;
    floorPid: string;
    userPid: string;
    roomOwnerPid: string;
    orgPid?: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const workplaceStore = useWorkplaceStore.getState();
      const currentUser = workplaceStore.user;

      if (!currentUser) {
        return { success: false, error: 'User not found' };
      }

      const payloadEvent = UserRoomAccessRequestEvent.create({
        user_pid: payload.userPid,
        room_pid: payload.roomPid,
        room_owner_pid: payload.roomOwnerPid,
        floor_pid: payload.floorPid,
        org_pid: payload.orgPid || workplaceStore.org?.pid,
        request_timestamp: new Date().toISOString(),
        message: `${currentUser.name} is requesting access to your room`,
      });

      // Send room access request event
      await GlobalEventService.emitTyped(payloadEvent, true);

      Logger.debug(
        `✅ Room access request sent to ${payload.roomOwnerPid} for room ${payload.roomPid}`
      );

      // Show confirmation to the requesting user
      const showSnackbar = useNotificationStore.getState().showSnackbar;
      showSnackbar('Access request sent to room owner', 'info');

      return { success: true };
    } catch (error) {
      Logger.error('❌ Error sending room access request:', error);
      return { success: false, error: 'Failed to send access request' };
    }
  }

  async emitCxTickerMessageActionEvent(
    action: 'created' | 'updated' | 'deleted',
    messageData: any
  ): Promise<void> {
    const orgPid = useWorkplaceStore.getState().org?.pid || '';
    const currentFloorId = useMyFloorStore.getState().currentFloorId;

    if (!currentFloorId) {
      Logger.error('[OfficeService] Cannot emit CxTickerMessageActionEvent: No current floor ID');
      return;
    }

    const event = CxTickerMessageActionEvent.create({
      org_pid: orgPid,
      floor_pid: currentFloorId,
      action,
      active_messages: messageData,
    });

    await GlobalEventService.emitTyped(event, true);
  }

  /**
   * Navigate user to a specific room
   * REPLACES: AppService.navigateToRoom
   */
  async navigateToRoom(payload: {
    roomPid: string;
    floorPid: string;
    userPid: string;
    floor?: any;
    room?: any;
    bypassLockCheck?: boolean; // Add flag to bypass lock check for approved access
    skipMeetingJoin?: boolean; // Skip joining meeting (e.g. navigating while on break)
    skipKnockSound?: boolean; // Suppress knock sound (e.g. when navigating via people search)
    forceRestore?: boolean; // Refresh restore: re-assert presence even in the current room, leave locks alone
    source?: RoomNavigationSource; // Where the move started, for analytics
  }): Promise<{ success: boolean; error?: string }> {
    const source = payload.forceRestore ? 'restore' : (payload.source ?? 'other');
    let bypassedLock = false;
    try {
      if (payload.skipKnockSound && this.officeEventRouter) {
        this.officeEventRouter.suppressKnockSound(3000);
      }

      // Check if user is already in the target room
      const workplaceState = useWorkplaceStore.getState();
      const currentRoomPid = workplaceState.currentRoom?.pid;

      if (currentRoomPid === payload.roomPid && !payload.forceRestore) {
        return { success: false, error: 'You are already in this room' };
      }

      // Check if the target room is locked (unless bypassing lock check for approved access)
      if (!payload.bypassLockCheck) {
        const lockStatus = this.isRoomLocked(payload.floorPid, payload.roomPid, payload.userPid);
        if (lockStatus.isLocked && lockStatus.ownerPid) {
          // Platform admins and owners bypass any lock, as before.
          //
          // CCaaS supervisors bypass locks **only on a CCaaS floor**. The role exists to
          // supervise agents on calls, so letting it open locked Webex or NexGen offices
          // would expand authorization well beyond what it was granted for. This check runs
          // for every room in the product, so the floor type has to be part of it.
          const targetFloorPlace = this.getStores().multiFloor.getFloorState(
            payload.floorPid
          )?.place;

          const canBypassLock =
            isCurrentUserPlatformAdminOrOwner() ||
            (isFloorPlaceTypeCcaas(targetFloorPlace?.type) &&
              isCurrentUserCcaasSupervisorOrAdminStatic());

          if (canBypassLock) {
            bypassedLock = true;
            // Can enter directly - show notification
            const showSnackbar = useNotificationStore.getState().showSnackbar;
            showSnackbar('🔓 Entering locked room directly', 'info');
            Logger.debug('✅ Admin/owner/supervisor bypassing room lock');
          } else {
            // Regular user - send access request
            const requestResult = await this.sendRoomAccessRequest({
              roomPid: payload.roomPid,
              floorPid: payload.floorPid,
              userPid: payload.userPid,
              roomOwnerPid: lockStatus.ownerPid,
              orgPid: useWorkplaceStore.getState().org?.pid,
            });

            if (requestResult.success) {
              trackRoomAccessRequested(source);
              return {
                success: false,
                error: `Access request sent to ${lockStatus.ownerName}. Please wait for approval.`,
              };
            } else {
              return {
                success: false,
                error: requestResult.error || 'Failed to send access request',
              };
            }
          }
        }
      }
      // 🔓 AUTO-UNLOCK LOGIC: If user is locked and moving to a different room, unlock them
      const workplaceStore = useWorkplaceStore.getState();
      const currentUserStatus = workplaceStore.getUserStatus();
      const stores = this.getStores();

      if (currentUserStatus === 'locked' && !payload.forceRestore) {
        // Show immediate "Unlocking room..." feedback
        const showSnackbar = useNotificationStore.getState().showSnackbar;
        showSnackbar('Unlocking room ...', 'info');

        // Get current floorPid before unlocking
        const unlockFloorPid =
          useMultiFloorStateStore.getState().currentFloorPresence ||
          workplaceStore.currentRoom?.floor_pid ||
          payload.floorPid;

        const currentRoomPid = workplaceStore.currentRoom?.pid;
        const isConferenceRoom = workplaceStore.currentRoom?.type?.toLowerCase() === 'conference';

        // For conference rooms: check if other locked users remain — if so, keep room locked
        const otherLockedUsersInRoom = isConferenceRoom
          ? (stores.multiFloor
              .getFloorState(unlockFloorPid)
              ?.presence?.users.filter(
                (u: any) =>
                  u.room === currentRoomPid && u.pid !== payload.userPid && u.status === 'locked'
              ) ?? [])
          : [];

        if (isConferenceRoom && otherLockedUsersInRoom.length > 0) {
          // Conference room still has other locked users — only update this user's status
          // without broadcasting a room-wide unlock
          const previousStatus = workplaceStore.previousStatus || 'available';

          await ApiService.request({
            method: 'PATCH',
            endpoint: '/workplace/user/presence',
            data: { status: previousStatus, message: '' },
            service: 'app',
          });

          workplaceStore.setUserStatus(previousStatus as any);

          // Emit with empty room_pid so other users in the conference room stay locked
          WorkplaceEventRouter.emitUserStatusChanged({
            user_pid: payload.userPid,
            status: previousStatus as any,
            org_pid: workplaceStore.org?.pid,
            floor_pid: unlockFloorPid,
            room_pid: '',
            message: '',
          });

          if (unlockFloorPid) {
            stores.multiFloor.updateUserStatus(unlockFloorPid, payload.userPid, previousStatus);
          }

          Logger.debug(
            `✅ User left locked conference room but ${otherLockedUsersInRoom.length} users remain — room stays locked`
          );
        } else {
          // Home room OR last user leaving a locked conference room — full unlock.
          // Restore the previous status directly instead of going through setRoomLocked(),
          // which is gated by canLockCurrentRoom() and no-ops for a visitor leaving a room
          // they don't own — silently leaving the room owner's status (and avatar ring)
          // stuck on 'locked' since the UserStatusChanged event never gets emitted.
          const restoredStatus = workplaceStore.previousStatus || 'available';

          await ApiService.request({
            method: 'PATCH',
            endpoint: '/workplace/user/presence',
            data: { status: restoredStatus, message: '' },
            service: 'app',
          });

          workplaceStore.setUserStatus(restoredStatus as any);

          WorkplaceEventRouter.emitUserStatusChanged({
            user_pid: payload.userPid,
            status: restoredStatus as any,
            org_pid: workplaceStore.org?.pid,
            // Required: receivers drop status events without a floor_pid.
            floor_pid: unlockFloorPid,
            room_pid: currentRoomPid || '',
            message: '',
          });

          if (unlockFloorPid && payload.userPid) {
            stores.multiFloor.updateUserStatus(unlockFloorPid, payload.userPid, restoredStatus);
          }

          Logger.debug('✅ User auto-unlocked when moving to different room');
        }

        try {
          // Play unlock sound
          GlobalAudioNotificationService.play('unlock');

          // Sync the lock status with myFloorStore and show final notification
          const myFloorStore = useMyFloorStore.getState();
          myFloorStore.syncLockStatusFromWorkplace();
          myFloorStore.setShowLockNotification(true); // Show unlock notification
        } catch (error) {
          Logger.warn('[OfficeService] Could not sync lock status or play audio:', error);
        }
      }

      const currentFloorPid = useMultiFloorStateStore.getState().currentFloorPresence;
      const targetFloorPid = payload.floorPid;
      const isFloorChange = currentFloorPid && currentFloorPid !== targetFloorPid;

      // 🚪 EMIT FLOOR LEAVE EVENT: If changing floors, emit leave event for current floor
      if (isFloorChange && currentFloorPid) {
        // Clear currentFloorPresence immediately so that any React renders during the
        // async FloorLeaveEvent await below use homeFloorPid as the policy floor instead
        // of the departing floor.  Without this, a Jitsi mute event arriving during the
        // await can fire the always-video useEffect with the old floor's policy still
        // active and force the camera back on right before the user reaches the new room.
        stores.multiFloor.setCurrentFloorPresence(null);

        try {
          const floorLeaveEvent = FloorLeaveEvent.create({
            floor_pid: currentFloorPid,
            user_pid: payload.userPid,
            org_pid: useWorkplaceStore.getState().org?.pid,
            room_pid: useWorkplaceStore.getState().presence?.room_pid,
          });

          await GlobalEventService.emitTyped(floorLeaveEvent, true);
        } catch (error) {
          Logger.error('❌ [FLOOR-EVENTS] Failed to emit FloorLeaveEvent:', error);
        }
      }

      // 1. Optimistic UI updates (immediate feedback)
      Logger.debug('🚀 [DEBUG] Step 1: Starting optimistic UI updates...');

      Logger.debug('🚀 [DEBUG] Calling stores.multiFloor.updateUserPresence...');
      const initialFloorId = useMultiFloorStateStore.getState().currentFloorPresence;
      stores.myFloor.setCurrentFloorId(payload.floorPid);

      // Update currentFloorPresence explicitly before updating user presence
      stores.multiFloor.setCurrentFloorPresence(payload.floorPid);

      stores.multiFloor.updateUserPresence(payload.floorPid, payload.roomPid, payload.userPid);
      Logger.debug('🚀 [DEBUG] ✅ stores.multiFloor.updateUserPresence completed');

      // 🔒 CHECK IF ROOM OWNER IS LOCKED: If owner has locked status, lock the entering user
      const floor = stores.multiFloor.getFloorState(payload.floorPid);
      const presence = floor?.presence;
      if (presence && !payload.forceRestore) {
        // Find users in the target room
        const usersInRoom = presence.users.filter((user: any) => user.room === payload.roomPid);
        // Check if any user in the room has 'locked' status (room owner)
        const lockedOwner = usersInRoom.find(
          (user: any) => user.status === 'locked' && user.pid !== payload.userPid
        );

        if (lockedOwner) {
          // Room owner is locked, so lock the entering user
          Logger.debug(
            `[OfficeService] 🔒 Room owner ${lockedOwner.pid} is locked, locking entering user ${payload.userPid}`
          );

          try {
            // Update status in backend
            await ApiService.request({
              method: 'PATCH',
              endpoint: '/workplace/user/presence',
              data: {
                status: 'locked',
                message: 'Room locked by owner',
              },
              service: 'app',
            });

            // Update local stores
            workplaceStore.setUserStatus('locked');
            stores.multiFloor.updateUserStatus(
              payload.floorPid,
              payload.userPid,
              'locked',
              'Room locked by owner'
            );

            // Emit event to notify other users about the status change
            const userContext = workplaceStore.getUserContext();
            WorkplaceEventRouter.emitUserStatusChanged({
              user_pid: payload.userPid,
              status: 'locked',
              org_pid: userContext.org_pid,
              floor_pid: payload.floorPid,
              room_pid: payload.roomPid,
              message: 'Room locked by owner',
            });

            Logger.debug(
              `[OfficeService] ✅ User ${payload.userPid} status changed to locked and event emitted`
            );
          } catch (error) {
            Logger.error('[OfficeService] ❌ Error locking user entering locked room:', error);
          }
        } else if (currentUserStatus === 'locked') {
          // User was locked and moving to a different room, unlock them
          // Small delay to ensure user presence is updated before status update
          await new Promise((resolve) => setTimeout(resolve, 50));
          stores.multiFloor.updateUserStatus(payload.floorPid, payload.userPid, 'active');
          Logger.debug('🚀 [DEBUG] ✅ User status updated to active in target floor');
        }
      }

      // Update workplace store (current room)
      Logger.debug('🚀 [DEBUG] Checking payload.room:', payload.room);
      const findTargetRoom = () =>
        useMultiFloorStateStore
          .getState()
          .getFloorState(payload.floorPid)
          ?.place?.rooms?.find((r: any) => r.pid === payload.roomPid);
      let roomToSet = payload.room ?? findTargetRoom();
      if (!roomToSet) {
        // the target floor's place may not be loaded — e.g. after a refresh on
        // another floor, Home targets a home floor that was never fetched. Without its
        // rooms, currentRoom (room name/background) was silently left on the old room.
        await stores.multiFloor.fetchPlace(payload.floorPid).catch(() => undefined);
        roomToSet = findTargetRoom();
      }
      if (roomToSet) {
        Logger.debug('🚀 [DEBUG] Updating workplace store current room...');
        useWorkplaceStore.getState().setCurrentRoom(roomToSet);
        Logger.debug('🚀 [DEBUG] ✅ Workplace store updated');
      } else {
        Logger.debug('🚀 [DEBUG] ⚠️ No room found for roomPid, skipping workplace store update');
      }

      // Update meeting store (current room ID)
      Logger.debug('🚀 [DEBUG] Checking meeting store availability...');
      if (useMeetingStore?.getState()?.setCurrentRoomId) {
        Logger.debug('🚀 [DEBUG] Updating meeting store current room ID...');
        // NGE-681: auditorium rooms now render the standard meeting view; the join
        // flow swaps in a role-scoped token for them (see joinJitsiMeeting).
        useMeetingStore
          .getState()
          .setCurrentRoomId(payload.roomPid, { skipMeetingJoin: payload.skipMeetingJoin });
        Logger.debug('🚀 [DEBUG] ✅ Meeting store updated');
      } else {
        Logger.debug('🚀 [DEBUG] ⚠️ Meeting store not available or no setCurrentRoomId method');
      }

      Logger.debug('🚀 [DEBUG] ✅ Step 1 completed: Optimistic UI updates done');

      // 2. Emit event through router (notifies other apps)
      Logger.debug('🚀 [DEBUG] Step 2: Starting event emission...');

      Logger.debug('🚀 [DEBUG] Creating UserRoomChangedEvent...');
      const roomChangedEvent = UserRoomChangedEvent.create({
        user_pid: payload.userPid,
        room_pid: payload.roomPid,
        floor_pid: payload.floorPid,
        // previous_room_pid is optional - could be added later if needed
      });
      Logger.debug('🚀 [DEBUG] Created event:', roomChangedEvent);

      // Use the router's emitEvent method (following working pattern)
      Logger.debug('🚀 [DEBUG] Checking officeEventRouter availability:', !!this.officeEventRouter);
      if (this.officeEventRouter) {
        Logger.debug('🚀 [DEBUG] Emitting event through router...');
        this.officeEventRouter.emitEvent(EventTypes.UserRoomChanged, roomChangedEvent);
        Logger.debug('🚀 [DEBUG] ✅ Event emitted successfully');
      } else {
        Logger.debug('🚀 [DEBUG] ⚠️ No officeEventRouter available!');
      }

      Logger.debug('🚀 [DEBUG] ✅ Step 2 completed: Event emission done');

      // 3. Make API calls (sync with server)
      Logger.debug('🚀 [DEBUG] Step 3: Starting API calls...');
      Logger.debug('🚀 [DEBUG] About to call Promise.all with API methods...');

      await this.updateSelfUserPresence(payload.floorPid, payload.roomPid);
      Logger.debug('🚀 [DEBUG] ✅ updateSelfUserPresence API call completed');

      if (initialFloorId !== payload.floorPid) {
        await stores.multiFloor.fetchPresence(payload.floorPid);
      }

      Logger.debug('🚀 [DEBUG] ✅ Step 3 completed: API calls done');

      // 📍 EMIT FLOOR ARRIVE EVENT: If changing floors or entering any floor, emit arrive event
      _log.debug('🚀 ~ OfficeService ~ isFloorChange:', { isFloorChange, currentFloorPid });
      if (isFloorChange || !currentFloorPid) {
        try {
          const floorArriveEvent = FloorArriveEvent.create({
            floor_pid: targetFloorPid,
            user_pid: payload.userPid,
            org_pid: useWorkplaceStore.getState().org?.pid,
            room_pid: payload.roomPid,
          });

          await GlobalEventService.emitTyped(floorArriveEvent, true);
        } catch (error) {
          Logger.error('❌ [FLOOR-EVENTS] Failed to emit FloorArriveEvent:', error);
        }
      }

      Logger.debug('✅ Room navigation completed successfully');
      trackRoomNavigated({
        source,
        isFloorChange: !!isFloorChange,
        roomType: roomToSet?.type ?? null,
        floorType: stores.multiFloor.getFloorState(payload.floorPid)?.place?.type ?? null,
        bypassedLock,
      });
      return { success: true };
    } catch (error) {
      Logger.error('❌ Room navigation failed:', error);
      Logger.error('🚀 [DEBUG] Error stack:', (error as Error)?.stack);
      Logger.error('🚀 [DEBUG] Error message:', (error as Error)?.message);

      // TODO: Implement rollback logic here
      // - Revert optimistic updates
      // - Show user feedback

      this.handleError('Room navigation failed', error);
      return { success: false, error: (error as Error)?.message || 'Unknown error' };
    }
  }

  /**
   * Handle presence updates from external events
   */
  async handlePresenceUpdate(presenceData: any): Promise<void> {
    try {
      _log('Handling presence update:', presenceData);
      Logger.debug('🚀 ~ OfficeService ~ presenceData:', presenceData);

      const floorPid = presenceData.floor_pid;
      if (!floorPid) {
        _log('⚠️  No floor_pid in presence data');
        return;
      }

      const requestSeq = (this.presenceRequestSeq.get(floorPid) ?? 0) + 1;
      this.presenceRequestSeq.set(floorPid, requestSeq);

      const result = await this.apiService.get(`office/floor/${floorPid}/presence`);
      _log('Presence data fetched:', result);

      // A newer request for this floor already applied its (more current) result while
      // this one was in flight — applying this stale one now would revert that update.
      const lastAppliedSeq = this.presenceAppliedSeq.get(floorPid) ?? 0;
      if (requestSeq <= lastAppliedSeq) {
        _log(
          `⏭️  Discarding stale presence fetch for floor ${floorPid} (seq ${requestSeq} <= applied ${lastAppliedSeq})`
        );
        return;
      }
      this.presenceAppliedSeq.set(floorPid, requestSeq);

      const stores = this.getStores();
      stores.multiFloor.setPresence(result.pid, result);

      if ((presenceData.user_pid || presenceData.guest_pid) && presenceData.room_pid) {
        stores.multiFloor.updateUserPresence(
          presenceData.floor_pid,
          presenceData.room_pid,
          presenceData.user_pid || presenceData.guest_pid
        );

        // Note: Guest background updates are now handled in GuestWorkplaceService.handleGuestNavigation
        // to avoid duplicate calls and ensure proper sequencing
      }
    } catch (error) {
      _log('❌ Failed to handle presence update:', error);
      this.handleError('Failed to handle presence update', error);
      throw error;
    }
  }

  /**
   * Handle presence updates from external events without API call
   * Uses provided presence data directly instead of fetching from API
   */
  async handlePresenceUpdateDirect(
    presenceData: FloorPresenceDto & {
      floor_pid: string;
      user_pid?: string;
      guest_pid?: string;
      room_pid?: string;
    }
  ): Promise<void> {
    try {
      _log('Handling presence update (direct):', presenceData);
      Logger.debug('🚀 ~ OfficeService ~ presenceData (direct):', presenceData);

      const floorPid = presenceData.floor_pid || presenceData.pid;
      if (!floorPid) {
        _log('⚠️  No floor_pid in presence data');
        return;
      }

      const stores = this.getStores();
      stores.multiFloor.setPresence(presenceData.pid, presenceData);

      // Handle guest presence update separately from user presence
      if (presenceData.guest_pid && presenceData.room_pid && presenceData.floor_pid) {
        // Find the guest in the presence data
        const floor = stores.multiFloor.getFloorState(presenceData.floor_pid);
        const guest = floor?.presence?.guests?.find((g: any) => g.pid === presenceData.guest_pid);

        if (guest) {
          // Update guest presence with new room location
          stores.multiFloor.updateGuestPresence(presenceData.floor_pid, {
            ...guest,
            room: presenceData.room_pid,
            status: (guest as any).status || 'active',
          } as FloorPresenceUserDto);
          Logger.debug('✅ Guest avatar moved to room:', presenceData.room_pid);
        } else {
          console.warn('⚠️ Guest not found in floor presence:', presenceData.guest_pid);
        }
      } else if (presenceData.user_pid && presenceData.room_pid && presenceData.floor_pid) {
        // Handle regular user presence update
        stores.multiFloor.updateUserPresence(
          presenceData.floor_pid,
          presenceData.room_pid,
          presenceData.user_pid
        );
        Logger.debug('✅ User avatar moved to room:', presenceData.room_pid);
      }
    } catch (error) {
      _log('❌ Failed to handle presence update (direct):', error);
      this.handleError('Failed to handle presence update (direct)', error);
      throw error;
    }
  }

  /**
   * Update guest presence in Zustand state without HTTP fetch
   * Moves guest from current room to target room
   */
  updateGuestPresenceInState(
    guestPid: string,
    targetFloorPid: string,
    targetRoomPid: string
  ): void {
    try {
      _log('Updating guest presence in state:', { guestPid, targetFloorPid, targetRoomPid });

      const stores = this.getStores();
      const floors = stores.multiFloor.floors;

      // Find the guest in all floors
      let currentFloorPid: string | null = null;
      let guestData: any = null;

      for (const [floorPid, floor] of Object.entries(floors)) {
        const guest = floor.presence?.guests?.find((g: any) => g.pid === guestPid);
        if (guest) {
          currentFloorPid = floorPid;
          guestData = guest;
          break;
        }
      }

      if (!guestData) {
        _log('⚠️ Guest not found in any floor presence:', guestPid);
        // This client never received the guest (e.g. missed their lobby entry), so there's
        // nothing to move locally — re-fetch the target floor's presence instead, but only
        // for a floor this client already tracks.
        if (floors[targetFloorPid]) {
          this.handlePresenceUpdate({
            floor_pid: targetFloorPid,
            guest_pid: guestPid,
            room_pid: targetRoomPid,
          }).catch((error) => _log('❌ Failed to re-fetch presence for unknown guest:', error));
        }
        return;
      }

      _log('Found guest in floor:', currentFloorPid, 'moving to:', targetFloorPid);

      // 🔓 A guest moved by the host (e.g. "Send to Lobby") is leaving whatever room they
      // were in, locked or not — carrying a stale 'locked' status forward here would leave
      // their ring stuck red on the host's own view since this is the only place that
      // patches presence.guests for this flow (sendGuestToLobby never does it locally).
      const wasLocked = guestData.status === 'locked';
      const previousRoomPid = guestData.room;
      const movingRooms = previousRoomPid !== targetRoomPid;
      const nextStatus = wasLocked && movingRooms ? 'active' : guestData.status;

      // If changing floors, remove from current floor and add to new floor
      if (currentFloorPid !== targetFloorPid) {
        // Remove from current floor
        const currentFloor = floors[currentFloorPid];
        if (currentFloor?.presence) {
          const updatedGuests =
            currentFloor.presence.guests?.filter((g: any) => g.pid !== guestPid) || [];
          stores.multiFloor.setPresence(currentFloorPid, {
            ...currentFloor.presence,
            guests: updatedGuests,
          });
        }

        // Add to new floor
        const targetFloor = floors[targetFloorPid];
        if (targetFloor?.presence) {
          const updatedGuest = {
            ...guestData,
            room: targetRoomPid,
            status: nextStatus,
            updated_at: new Date(),
          };
          const updatedGuests = [...(targetFloor.presence.guests || []), updatedGuest];
          stores.multiFloor.setPresence(targetFloorPid, {
            ...targetFloor.presence,
            guests: updatedGuests,
          });
        }
      } else {
        // Same floor, just update room
        stores.multiFloor.updateGuestPresence(targetFloorPid, {
          ...guestData,
          room: targetRoomPid,
          status: nextStatus,
        });
      }

      _log('✅ Guest presence updated in state');
    } catch (error) {
      _log('❌ Failed to update guest presence in state:', error);
    }
  }

  /**
   * Update active guests list in workplace store
   */
  async updateActiveGuestsList(): Promise<void> {
    if (isCurrentUserGuest()) {
      Logger.debug('[OfficeService] ⏭️ Skipping active guests fetch for guest user');
      return;
    }
    try {
      Logger.debug('[OfficeService] 🔄 Updating active guests list...');

      const activeGuests = await this.apiService.get(`workplace/guest/presence/active-by-host`);
      Logger.debug('[OfficeService] ✅ Active guests fetched:', activeGuests);

      const { addGuestUser } = useWorkplaceStore.getState();
      addGuestUser(activeGuests);

      Logger.debug('[OfficeService] ✅ Active guests list updated in store');
    } catch (error) {
      Logger.error('[OfficeService] ❌ Error updating active guests list:', error);
    }
  }

  /**
   * Update user's floor presence (room location)
   */
  async updateFloorPresence(userPid: string, roomPid: string | null): Promise<void> {
    try {
      Logger.debug('Updating floor presence:', { userPid, roomPid });

      // Update store optimistically
      const currentFloorPid = useFloorManagerStore.getState().getMyFloorPid();
      if (currentFloorPid) {
        this.getStores().multiFloor.updateUserPresence(currentFloorPid, roomPid || '', userPid);
      }

      // Make API call to update server
      // Note: Implement this method or use existing updateSelfUserPresence
      if (currentFloorPid && roomPid) {
        await this.updateSelfUserPresence(currentFloorPid, roomPid);
      }
    } catch (error) {
      Logger.error('❌ Failed to update floor presence:', error);
      this.handleError('Failed to update floor presence', error);
      throw error;
    }
  }

  /**
   * Load initial floor data (place + presence)
   * REPLACES: MultiFloorStore.loadInitialState
   */
  async loadFloorData(floorPid: string): Promise<void> {
    try {
      Logger.debug('Loading floor data:', floorPid);

      // Ensure floor is added to manager
      this.getStores().floorManager.addFloor(floorPid);

      // Load both place and presence data
      await Promise.all([this.fetchFloorPlace(floorPid), this.fetchFloorPresence(floorPid)]);

      // Mark as current floor
      this.getStores().floorManager.markMyFloor(floorPid);
    } catch (error) {
      Logger.error('❌ Failed to load floor data:', error);
      this.handleError('Failed to load floor data', error);
      throw error;
    }
  }

  /**
   * Fetch and update floor presence data
   * REPLACES: MultiFloorStore.fetchPresence
   */
  async fetchFloorPresence(floorPid: string): Promise<void> {
    try {
      Logger.debug('Fetching floor presence:', floorPid);

      const presence = await this.apiService.get<FloorPresenceDto>(
        `/office/floor/${floorPid}/presence`
      );
      Logger.debug('🚀 ~ OfficeService ~ presence:', presence);

      // Update stores
      const stores = this.getStores();
      stores.multiFloor.setPresence(floorPid, presence);

      // Reconcile the current user's own status against this authoritative floor-presence
      // data. The bootstrap/profile API's presence.status can fall out of sync with this
      // endpoint (e.g. it reports 'active' while this endpoint correctly shows the user is
      // 'locked' in their current room), leaving a stale ring on the user's own screen even
      // though every other client sees the correct color from this same data.
      const workplaceStore = useWorkplaceStore.getState();
      const currentUserPid = workplaceStore.user?.pid;
      const currentRoomPid = workplaceStore.currentRoom?.pid;
      if (currentUserPid && currentRoomPid) {
        const usersInRoom = (presence.users ?? []).filter((u: any) => u.room === currentRoomPid);
        const roomIsLocked = usersInRoom.some((u: any) => u.status === 'locked');
        const selfEntry = usersInRoom.find((u: any) => u.pid === currentUserPid);
        const authoritativeStatus = roomIsLocked ? 'locked' : selfEntry?.status;

        if (authoritativeStatus && authoritativeStatus !== workplaceStore.user.status) {
          Logger.debug(
            `[OfficeService] 🔄 Reconciling own status: bootstrap said '${workplaceStore.user.status}', floor presence says '${authoritativeStatus}'`
          );
          workplaceStore.setUserStatus(authoritativeStatus as any);
        }
      }
    } catch (error) {
      Logger.error('❌ Failed to fetch floor presence:', error);
      this.handleError('Failed to fetch floor presence', error);
      throw error;
    }
  }

  /**
   * Fetch floor place data (layout, rooms, etc.)
   * REPLACES: MultiFloorStore.fetchPlace
   */
  async fetchFloorPlace(floorPid: string): Promise<FloorPlaceDto> {
    try {
      const place = await this.apiService.get<FloorPlaceDto>(`/office/floor/${floorPid}/place`);
      this.getStores().multiFloor.setPlace(floorPid, place);

      return place;
    } catch (error) {
      Logger.error('❌ Failed to fetch floor place:', error);
      this.handleError('Failed to fetch floor place', error);
      throw error;
    }
  }

  /**
   * Update guest presence on floor
   */
  async updateGuestPresence(
    floorPid: string,
    guest: FloorPresenceUserDto,
    orgPid: string,
    roomPid: string
  ): Promise<void> {
    try {
      await ApiService.patch(
        '/workplace/guest/presence',
        {
          guest_pid: guest,
          room_pid: roomPid,
          floor_pid: floorPid,
          status: 'active',
          message: 'Guest arrived to the room',
        }
        // {
        //   config: { // comment for the config into the headers, because it overrides the all headers
        //     headers: {
        //       'x-org-pid': orgPid,
        //     },
        //   },
        // }
      );

      this.getStores().multiFloor.updateGuestPresence(floorPid, guest);

      if (!isCurrentUserGuest()) {
        const { addGuestUser } = useWorkplaceStore.getState();

        // Update active guests list when presence changes
        try {
          const activeGuests = await this.apiService.get(`workplace/guest/presence/active-by-host`);

          Logger.debug('🚀 ~ OfficeService ~ activeGuests updated:', activeGuests);
          addGuestUser(activeGuests);
        } catch (error) {
          Logger.error('❌ Error updating active guests:', error);
        }
      }
    } catch (error) {
      Logger.error('❌ Failed to update guest presence:', error);
      this.handleError('Failed to update guest presence', error);
      throw error;
    }
  }

  /**
   * The guest side of a host-initiated logout.
   *
   * The API call and the sign-out are best-effort and each own their try/catch: the host
   * has usually already ended this guest's session server-side, so the logout POST comes
   * back 401 more often than not. Neither may take the cleanup and the redirect down with
   * it — without them LoggedOutGuard bounces /welcome straight back to /guest-workplace
   * and the guest never leaves the room they were just removed from.
   */
  async updateGuestLogoff(guestPid: string, orgPid: string): Promise<void> {
    try {
      await ApiService.request({
        method: 'POST',
        endpoint: 'welcome/guest/logout',
        data: {
          guest_pid: guestPid,
        },
        userType: UserType.GuestUser,
        config: {
          headers: {
            'x-org-pid': orgPid,
          },
        },
      });
    } catch (error) {
      Logger.error('[OfficeService] ❌ Guest logout API call failed, logging off anyway:', error);
    }

    try {
      const { signOut } = useAuthStore.getState();
      await signOut();
    } catch (error) {
      Logger.error('[OfficeService] ❌ signOut failed, logging off anyway:', error);
    }

    try {
      useGuestStore.getState().resetGuest();
    } catch (error) {
      Logger.error('[OfficeService] ❌ resetGuest failed, logging off anyway:', error);
    }

    // Clears the guest localStorage and lands on the org's guest login form.
    forceGuestLogoutRedirect();
  }

  /**
   * Handle OrgCxAgentsStatisticsCollected events (internal app events)
   */
  async handleOrgCxAgentsStatisticsCollected(): Promise<void> {
    try {
      if (isCurrentUserGuest()) {
        Logger.debug('Skipping KPIs fetch for guest user');
        return;
      }

      const { setIsLoadingAgentsKPIsData, setAgentsKpisData } = useCxFloorStore.getState();
      const currentFloorId = useMyFloorStore.getState().currentFloorId;
      const currentFloorPlace = useMultiFloorStateStore
        .getState()
        .getFloorState(currentFloorId || '')?.place;
      const floors = useMultiFloorStateStore.getState().floors;

      // Only fetch KPIs if the user has Cx floors
      if (floors && Object.keys(floors).length > 0) {
        if (
          Object.values(floors).some((floor) => isFloorPlaceTypeCcaas(floor.place?.type)) &&
          isFloorPlaceTypeCcaas(currentFloorPlace?.type)
        ) {
          setIsLoadingAgentsKPIsData(true);
          try {
            // Pass current floor PID to fetch only agents on current floor
            const floorPids = currentFloorId ? [currentFloorId] : undefined;
            const data = await fetchLatestKpisForAllAgents(floorPids);
            setAgentsKpisData(data);
          } catch (error) {
            Logger.error('Error fetching KPIs:', error);
          } finally {
            setIsLoadingAgentsKPIsData(false);
          }

          // Dialer statuses refresh on the same signal. Kept separate from the KPI fetch so
          // a failure here can't blank the KPIs, and so orgs whose provider reports no agent
          // states simply get an empty map, exactly as before this existed.
          try {
            const { setAgentStatuses } = useCxFloorStore.getState();
            const statuses = await fetchAgentStatuses();
            setAgentStatuses(statuses);
            syncOwnCallLock(statuses, useWorkplaceStore.getState().user?.pid);
          } catch (error) {
            Logger.error('Error fetching CX agent statuses:', error);
          }
        }
      }
    } catch (error) {
      Logger.error('❌ Error handling OrgCxAgentsStatisticsCollected event:', error);
    }
  }

  /**
   * Apply a live agent-status change pushed from the backend.
   *
   * Merges rather than replaces: the event only carries the agents whose status actually
   * changed, so everyone else's stays as it was. That keeps the payload small — a tick where
   * one agent picks up a call sends one entry, not the whole floor.
   */
  handleCxAgentStatusChanged(event: any): void {
    try {
      const payload = event?.data?.payload ?? event?.payload ?? event;
      const statuses = payload?.statuses;

      if (!Array.isArray(statuses) || statuses.length === 0) {
        return;
      }

      const { agentStatuses, setAgentStatuses } = useCxFloorStore.getState();
      const next = { ...agentStatuses };

      for (const entry of statuses) {
        if (!entry?.user_pid || !entry?.status) continue;
        next[entry.user_pid] = { status: entry.status, as_of: entry.as_of };
      }

      setAgentStatuses(next);
      syncOwnCallLock(next, useWorkplaceStore.getState().user?.pid);
      Logger.debug(`[OfficeService] Applied ${statuses.length} CX agent status change(s)`);
    } catch (error) {
      Logger.error('Error applying CX agent status change:', error);
    }
  }

  /**
   * Handle CxTickerMessageAction events (internal app events)
   */
  async handleCxTickerMessageAction(event: any): Promise<void> {
    try {
      Logger.debug('[OfficeService] Handling CxTickerMessageAction event:', event);
      if (isCurrentUserGuest()) {
        Logger.debug('Skipping CxTickerMessageAction for guest user');
        return;
      }

      const { fetchActiveMessages } = useCxTickerMessageStore.getState();
      const currentFloorId = useMyFloorStore.getState().currentFloorId;
      const currentFloorPlace = useMultiFloorStateStore
        .getState()
        .getFloorState(currentFloorId || '')?.place;

      // Only process if current floor is CCaaS type
      if (currentFloorId && isFloorPlaceTypeCcaas(currentFloorPlace?.type)) {
        await fetchActiveMessages(currentFloorId);
      }
    } catch (error) {
      Logger.error('❌ Error handling CxTickerMessageAction event:', error);
    }
  }

  /**
   * Clear all presence data for a floor
   */
  async clearFloorPresence(floorPid: string): Promise<void> {
    try {
      _log('Clearing floor presence for floor:', floorPid);

      const stores = this.getStores();
      stores.multiFloor.clearPresence(floorPid);

      _log('✅ Floor presence cleared successfully');
    } catch (error) {
      this.handleError('Failed to clear floor presence', error, { floorPid });
    }
  }

  /**
   * Remove a user from office presence (when they logout)
   */
  async removeUserFromPresence(userPid: string): Promise<void> {
    try {
      _log('Removing user from office presence:', userPid);

      const stores = this.getStores();
      stores.multiFloor.removeUserFromPresence(userPid);

      _log('✅ User removed from office presence successfully');
    } catch (error) {
      this.handleError('Failed to remove user from office presence', error, { userPid });
    }
  }

  /**
   * Update user's presence on the server
   * Uses existing function from nexgenCX service
   * Also detects floor changes and emits FloorLeaveEvent when appropriate
   */
  private async updateSelfUserPresence(floorPid: string, roomPid: string | null): Promise<void> {
    try {
      Logger.debug('🔄 Calling API to update user presence:', { floorPid, roomPid });

      // 🏢 FLOOR CHANGE DETECTION: Check if user is leaving a different floor
      const workplaceStore = useWorkplaceStore.getState();
      Logger.debug('🚀 ~ OfficeService ~ workplaceStore:', workplaceStore);
      const currentFloorPid = useMultiFloorStateStore.getState().currentFloorPresence;
      const targetFloorPid = floorPid;
      const isFloorChange = currentFloorPid && currentFloorPid !== targetFloorPid;

      Logger.debug('🏢 [FLOOR-EVENTS] Floor change detection in updateSelfUserPresence:', {
        currentFloorPid,
        targetFloorPid,
        isFloorChange,
      });

      // 🚪 EMIT FLOOR LEAVE EVENT: If changing floors, emit leave event for current floor
      if (isFloorChange && currentFloorPid) {
        try {
          const currentUserPid = workplaceStore.user?.pid;
          if (currentUserPid) {
            const floorLeaveEvent = FloorLeaveEvent.create({
              floor_pid: currentFloorPid,
              user_pid: currentUserPid,
              org_pid: workplaceStore.org?.pid,
              room_pid: workplaceStore.currentRoom?.pid,
            });

            await GlobalEventService.emitTyped(floorLeaveEvent, true);
          }
        } catch (floorLeaveError) {
          Logger.error(
            '❌ [FLOOR-EVENTS] Failed to emit FloorLeaveEvent from updateSelfUserPresence:',
            floorLeaveError
          );
          // Don't throw - presence update should continue even if event fails
        }
      }

      // Import and use the existing function
      await updateSelfUserPresence(floorPid, roomPid || '');

      Logger.debug('✅ API call successful - user presence updated');
    } catch (error) {
      Logger.error('❌ Failed to update self user presence:', error);
      throw error;
    }
  }

  /**
   * Private error handler - routes through event router (following working pattern)
   */
  private handleError(message: string, error: any, context?: any): void {
    if (this.officeEventRouter) {
      this.officeEventRouter.emitApplicationError(message, error, context);
    } else {
      // Fallback if router not set (shouldn't happen in normal operation)
      Logger.error('❌ No office event router available for error:', message, error);
    }
  }

  /**
   * Get store instances (for internal use)
   */
  public getStores() {
    return {
      multiFloor: useMultiFloorStateStore.getState(),
      floorManager: useFloorManagerStore.getState(),
      myFloor: useMyFloorStore.getState(),
    };
  }

  // Get ApiService instance (singleton pattern)
  private get apiService() {
    return ApiService.getInstance();
  }
}
